/**
 * Who is being told about a low balance — one rule, two surfaces (2026-09-13).
 *
 * THE DEFECT THIS CLOSES. Two places tell a user their balance is low: the
 * banner band at the top of the page (GlobalLowBalanceBanner) and the notice
 * directly above the chat composer. The band has known since it was written
 * that a member who cannot manage billing must get honest ask-an-owner copy
 * and NO button — "no dead-end button" is in its own comment. The chat notice
 * never learned it, and always offered "top up" / "add payment method".
 *
 * So a member without `billing.manage` on a phone read, at the top of the
 * screen, "ask an owner to top up" with no control — and three inches below,
 * against their composer, a top-up button that takes them somewhere they
 * cannot act. The two surfaces did not merely duplicate; they contradicted,
 * and the one that was wrong is the one attached to the thing they were trying
 * to do.
 *
 * The rule lives here, once, because the alternative is two derivations of one
 * question that agree until someone edits one of them. Same reason
 * NARROW_VIEWPORT_QUERY is a shared string rather than a breakpoint written
 * out twice: the 720-versus-820 dead zone came from exactly that.
 */

/** The three facts the answer depends on. Nothing here reads a store. */
export interface LowBalanceAudienceInput {
  /**
   * 'account' means the wallet on screen is the ORGANIZATION's, not this
   * person's own. Paying into someone else's wallet is the case that needs
   * permission; a personal wallet never does.
   */
  displayScope: string | null | undefined;
  /** Whether the permission lookup has resolved. */
  visibilityReady: boolean;
  /** Whether this viewer holds billing.manage (or is the owner). */
  canManageBilling: boolean;
}

/**
 * Is this the reader who must ask someone else to top up?
 *
 * FAIL-CLOSED ON `visibilityReady`: while the lookup is in flight the answer is
 * false, i.e. the reader is treated as able to pay and keeps the button. That
 * is deliberate and it is the direction the band already chose — flashing an
 * "ask an owner" message at a person who CAN pay, and then swapping it for a
 * button a moment later, is worse than the reverse. TopUpModal made the same
 * call for the same reason (its `usageVisibility.ready` guard, which exists
 * because an in-flight lookup flashed the blocked screen).
 */
export function isAskOwnerAudience(input: LowBalanceAudienceInput): boolean {
  return input.displayScope === 'account'
    && input.visibilityReady
    && !input.canManageBilling;
}
